import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { getToken, setToken, clearToken, registerLogoutHandler } from '../api/instanceApi';

// supplies 프로젝트의 client/contexts/AuthContext.tsx를 이 프로젝트(JS/CRA)에 맞게 포팅한 것
const AuthContext = createContext(null);

const USER_KEY = 'shield_user';
const IDLE_TIMEOUT = 2 * 60 * 60 * 1000; // 2시간 무활동 시 자동 로그아웃

function persist(token, user) {
  setToken(token);
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const idleTimer = useRef(null);

  const logout = useCallback(() => {
    setUser(null);
    clearToken();
    sessionStorage.removeItem(USER_KEY);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(logout, IDLE_TIMEOUT);
  }, [logout]);

  // 전역 401 핸들러 등록 (instanceApi와 연동)
  useEffect(() => {
    registerLogoutHandler(logout);
  }, [logout]);

  // 유휴 타이머 (로그인 중에만)
  useEffect(() => {
    if (!user) return undefined;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, resetIdleTimer));
    resetIdleTimer();
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
    };
  }, [user, resetIdleTimer]);

  // 마운트 시 토큰 검증 (sliding session)
  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    api
      .post('/auth/verify')
      .then((res) => {
        if (!res.data?.success) throw new Error();
        persist(res.data.data.token, res.data.data.user);
        setUser(res.data.data.user);
      })
      .catch(() => {
        clearToken();
        sessionStorage.removeItem(USER_KEY);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (id, password) => {
    let res;
    try {
      res = await api.post('/auth/login', { id, password });
    } catch (err) {
      throw new Error(err.response?.data?.message || '로그인에 실패했습니다.');
    }
    if (!res.data?.success) {
      throw new Error(res.data?.message || '로그인에 실패했습니다.');
    }
    persist(res.data.data.token, res.data.data.user);
    setUser(res.data.data.user);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
